import {
  Phone,
  MessageSquare,
  Network,
  Shield,
  Video,
  Cloud,
  Lock,
  Zap,
  Users,
  Building2,
  Headphones,
  BarChart3,
  type LucideIcon,
} from 'lucide-react'

export interface ServiceFeature {
  title: string
  description: string
  icon: LucideIcon
}

export interface Service {
  id: string
  title: string
  shortTitle: string
  description: string
  longDescription: string
  icon: LucideIcon
  href: string
  features: ServiceFeature[]
  benefits: string[]
  certifications?: string[]
}

export const services: Service[] = [
  // CYBERSECURITY FIRST - The foundation of all other solutions
  {
    id: 'cybersecurity',
    title: 'Cybersecurity Solutions',
    shortTitle: 'Cybersecurity',
    description: 'The foundation of every engagement. We assess your security posture first, then build your technology stack on a secure foundation.',
    longDescription: 'UC Advisors starts every engagement with a comprehensive security assessment. We believe you cannot have reliable communications or customer service without a secure foundation. Our cybersecurity solutions protect your business from evolving threats while ensuring compliance with financial services regulations.',
    icon: Shield,
    href: '/solutions/cybersecurity',
    features: [
      {
        title: 'Security Assessment',
        description: 'Comprehensive evaluation of your current security posture and risk profile.',
        icon: Shield,
      },
      {
        title: 'Endpoint Protection',
        description: 'Advanced threat detection and response for all devices in your organization.',
        icon: Lock,
      },
      {
        title: 'Email Security',
        description: 'Block phishing, malware, and spam before they reach your inbox.',
        icon: MessageSquare,
      },
      {
        title: 'Compliance Management',
        description: 'Meet NCUA, FFIEC, GLBA, and PCI DSS requirements with automated tools.',
        icon: Building2,
      },
    ],
    benefits: [
      'Foundation for all technology decisions',
      'Meet financial services compliance requirements',
      'Prevent data breaches and ransomware',
      'Reduce security incident response time',
      '24/7 security monitoring',
    ],
  },
  // NETWORK - Second layer, built on security
  {
    id: 'network',
    title: 'Network Solutions',
    shortTitle: 'Network',
    description: 'Build a robust, secure, and scalable network infrastructure that powers your digital transformation—architected with security in mind.',
    longDescription: 'UC Advisors designs and implements enterprise network solutions that provide the foundation for all your business communications and applications. Every network design incorporates the security principles established in our assessment phase, ensuring your infrastructure is both performant and protected.',
    icon: Network,
    href: '/solutions/network',
    features: [
      {
        title: 'Secure Network Design',
        description: 'Custom network architecture designed for performance, security, and scalability.',
        icon: Network,
      },
      {
        title: 'Wireless Solutions',
        description: 'Enterprise WiFi that delivers reliable coverage and high-speed connectivity.',
        icon: Zap,
      },
      {
        title: 'Network Security',
        description: 'Firewalls, intrusion detection, and segmentation to protect your data.',
        icon: Shield,
      },
      {
        title: 'Performance Monitoring',
        description: '24/7 network monitoring and proactive management to prevent downtime.',
        icon: BarChart3,
      },
    ],
    benefits: [
      'Security-first network architecture',
      'Maximize network uptime and reliability',
      'Support bandwidth-intensive applications',
      'Scale infrastructure on demand',
      'Reduce IT operational burden',
    ],
  },
  // SD-WAN - Third layer
  {
    id: 'sd-wan',
    title: 'SD-WAN Solutions',
    shortTitle: 'SD-WAN',
    description: 'Transform your WAN with intelligent, application-aware routing that optimizes performance while maintaining security across all locations.',
    longDescription: 'Software-Defined WAN technology from UC Advisors gives you centralized control over your entire network with integrated security. We help you move beyond expensive MPLS while ensuring your multi-site connectivity remains secure and compliant with financial services requirements.',
    icon: Cloud,
    href: '/solutions/sd-wan',
    features: [
      {
        title: 'Application-Aware Routing',
        description: 'Automatically prioritize critical applications for optimal performance.',
        icon: Zap,
      },
      {
        title: 'Multi-Link Support',
        description: 'Aggregate multiple internet connections for redundancy and bandwidth.',
        icon: Network,
      },
      {
        title: 'Centralized Management',
        description: 'Manage your entire WAN from a single cloud-based dashboard.',
        icon: Cloud,
      },
      {
        title: 'Integrated Security',
        description: 'Built-in firewall, encryption, and secure access for all traffic.',
        icon: Lock,
      },
    ],
    benefits: [
      'Reduce WAN costs by up to 70%',
      'Maintain security across all locations',
      'Improve application performance',
      'Simplify branch deployments',
      'Enable direct cloud access securely',
    ],
  },
  // UCaaS - Fourth layer, communications built on secure network
  {
    id: 'ucaas',
    title: 'Unified Communications as a Service',
    shortTitle: 'UCaaS',
    description: 'Enterprise-grade voice, video, messaging, and collaboration—deployed on the secure network foundation we helped you build.',
    longDescription: 'UC Advisors delivers comprehensive UCaaS solutions that bring together voice, video conferencing, team messaging, and file sharing into a single, seamless platform. As Zoom Certified installers and support specialists, we ensure your organization gets the most out of modern communication technology—built on the secure infrastructure we designed together.',
    icon: Phone,
    href: '/solutions/ucaas',
    features: [
      {
        title: 'HD Voice & Video',
        description: 'Crystal-clear voice calls and high-definition video conferencing.',
        icon: Video,
      },
      {
        title: 'Team Messaging',
        description: 'Real-time chat, file sharing, and collaboration channels.',
        icon: MessageSquare,
      },
      {
        title: 'Cloud PBX',
        description: 'Enterprise-grade phone system with advanced call routing and IVR.',
        icon: Cloud,
      },
      {
        title: 'Mobile Integration',
        description: 'Take your office anywhere with secure mobile apps.',
        icon: Phone,
      },
    ],
    benefits: [
      'Reduce communication costs by up to 50%',
      'Secure, encrypted communications',
      'Enable remote and hybrid work',
      'Improve team collaboration',
      '99.999% uptime SLA guarantee',
    ],
    certifications: ['Zoom Certified Installer', 'Zoom Support Specialist'],
  },
  // CCaaS - Fifth layer, customer experience on top of everything
  {
    id: 'ccaas',
    title: 'Contact Center as a Service',
    shortTitle: 'CCaaS',
    description: 'Deliver exceptional customer experiences with an intelligent, omnichannel contact center—secured and optimized by our holistic approach.',
    longDescription: 'Our CCaaS solutions empower your customer service teams with AI-powered tools, omnichannel capabilities, and real-time analytics. As Zoom Certified specialists, we implement and support industry-leading contact center platforms that transform customer engagement—all built on the secure, reliable infrastructure we\'ve optimized for you.',
    icon: Headphones,
    href: '/solutions/ccaas',
    features: [
      {
        title: 'Omnichannel Support',
        description: 'Engage customers across voice, chat, email, SMS, and social media.',
        icon: MessageSquare,
      },
      {
        title: 'AI-Powered Routing',
        description: 'Intelligent call routing that connects customers to the right agent.',
        icon: Zap,
      },
      {
        title: 'Real-Time Analytics',
        description: 'Live dashboards and reporting to monitor and optimize operations.',
        icon: BarChart3,
      },
      {
        title: 'Workforce Management',
        description: 'Forecast demand, schedule agents, and manage quality.',
        icon: Users,
      },
    ],
    benefits: [
      'Increase first-call resolution rates',
      'PCI DSS compliant call handling',
      'Reduce average handle time',
      'Improve customer satisfaction scores',
      'AI-powered self-service options',
    ],
    certifications: ['Zoom Certified Installer', 'Zoom Support Specialist'],
  },
]

export const getServiceById = (id: string) => services.find(s => s.id === id)
